import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { useChartSyncKey } from "./chart-sync.tsx";
import { useDialogFocus } from "./useDialogFocus.ts";
import {
  defaultNumberFormat,
  estimateSuffix,
  type Measure,
} from "./estimate.ts";

/**
 * The uPlot chart wrapper (design 12.1 "uPlot for dense time-series on canvas"; 12.4 "every chart
 * has a data-table alternative").
 *
 * Responsibilities, kept deliberately in one place so every screen chart behaves the same:
 *
 * - Owns a uPlot instance across the chart lifecycle: creates it on mount with sized options,
 *   updates data without a rebuild (`setData`), resizes with the container (ResizeObserver →
 *   `setSize`), rebuilds only when the structure (series shape, theme) changes, and destroys on
 *   unmount. uPlot is imperative; React owns the container, uPlot owns the canvas inside it.
 * - Synced crosshair: the chart joins the nearest `ChartSyncProvider`'s group via
 *   `cursor.sync.key`, so hovering one chart moves the crosshair on every chart on the screen. A
 *   lone chart gets its own isolated group.
 * - Data-table alternative: a visually-hidden, fully semantic `<table>` of the same x/series values
 *   is ALWAYS rendered next to the canvas (not a toggle that can be forgotten), so the chart is
 *   never the only way to read the data. A visible "Show data table" disclosure exposes the same
 *   table for sighted keyboard users too.
 * - Theme-driven: axis/grid/series colours are read from the theme CSS variables at build time and
 *   the chart is rebuilt when the theme changes (a `data-theme` change on `<html>`), so dark/light
 *   both look right with no hard-coded colours.
 * - Honest states: an empty window renders an explanatory panel ("No data in range") rather than a
 *   blank canvas; a `loading` flag shows a polite status. Reduced-motion is respected (uPlot does
 *   not animate; the DOM around it uses the theme's reduced-motion rule).
 *
 * The wrapper is screen-agnostic: it renders the `data`/`series` it is given. Pause/live lives in
 * the time-range provider (task 11.1); a paused screen simply passes the frozen window's data and
 * the chart shows it frozen. No data fetching happens here.
 */

/** uPlot's aligned data: `[xs, ...ys]`, every array the same length. */
export type ChartData = readonly (readonly number[])[];

/** A plotted series (y column). The x column is described by {@link ChartProps.x}. */
export interface ChartSeries {
  /** Legend/table label for the series. */
  readonly label: string;
  /**
   * The stroke colour. A `--token` name is resolved from the theme CSS variables; any other string
   * is used verbatim. Defaults to the accent colour so a caller need not pick one.
   */
  readonly colorVar?: string;
  /** Width of the line in CSS px. Defaults to 1.5. */
  readonly width?: number;
  /** Fill under the line (same resolution rules as `colorVar`); omitted means no fill. */
  readonly fillVar?: string;
  /** Confidence in this series' values, surfaced in the data table (design 12.3 "est."). */
  readonly measure?: Measure;
}

/** How the x axis is interpreted. `time` formats epoch-seconds/ms as clock time. */
export type XKind = "time" | "linear";

export interface ChartProps {
  /** Accessible name for the chart region and the data-table caption. */
  readonly title: string;
  /** Aligned data `[xs, ...ys]`; `xs.length` must match every y array. */
  readonly data: ChartData;
  /** One descriptor per y column (i.e. `data.length - 1` of them). */
  readonly series: readonly ChartSeries[];
  /** x-axis interpretation; `time` by default (the common case for telemetry). */
  readonly x?: XKind;
  /** x values are epoch milliseconds (`true`, default) or epoch seconds (`false`) when `x==="time"`. */
  readonly xIsMillis?: boolean;
  /** Fixed height in CSS px; width tracks the container. Defaults to 220. */
  readonly height?: number;
  /** Show a loading status instead of the chart (data still arriving). */
  readonly loading?: boolean;
  /** Override the empty-state cause (design 12.3). Defaults to "No data in range." */
  readonly emptyCause?: string;
  /** Format a y value for the data table / legend. Defaults to a grouped number. */
  readonly formatY?: (value: number) => string;
  /** Format an x value for the data table. Defaults to ISO time (`time`) or the number (`linear`). */
  readonly formatX?: (value: number) => string;
}

/** Resolve a `--token` to its computed value against `root`; pass other strings through. */
function resolveColor(root: Element, color: string | undefined, fallback: string): string {
  if (color === undefined) return fallback;
  if (!color.startsWith("--")) return color;
  const resolved = getComputedStyle(root).getPropertyValue(color).trim();
  return resolved.length > 0 ? resolved : fallback;
}

/**
 * The colour for a legend swatch. A `--token` series colour is emitted as `var(--token)` so the
 * swatch tracks the theme; anything else (a literal colour, or no colour set) falls back to
 * uPlot's own resolved series stroke/fill, exactly as its default legend does.
 */
function legendMarkerColor(
  colorVar: string | undefined,
  u: uPlot,
  seriesIdx: number,
  kind: "stroke" | "fill",
): string {
  // The chart resolves an unset token to the accent; mirror that so the swatch never goes blank.
  if (colorVar !== undefined && colorVar.startsWith("--")) return `var(${colorVar}, var(--accent))`;
  const s = u.series[seriesIdx];
  if (s === undefined) return "";
  const resolved = kind === "stroke" ? (s.width ? s.stroke : null) : s.fill;
  const value = typeof resolved === "function" ? resolved(u, seriesIdx) : resolved;
  return typeof value === "string" ? value : "";
}

/** Read the theme tokens the chart needs, resolved against the document root. */
interface ThemeColors {
  readonly axis: string;
  readonly grid: string;
  readonly text: string;
  readonly accent: string;
}

function readTheme(): ThemeColors {
  const root = document.documentElement;
  const style = getComputedStyle(root);
  const get = (name: string, fallback: string): string => {
    const v = style.getPropertyValue(name).trim();
    return v.length > 0 ? v : fallback;
  };
  return {
    axis: get("--border-strong", "#3a4654"),
    grid: get("--border", "#26303c"),
    text: get("--text-muted", "#9aa7b4"),
    accent: get("--accent", "#4aa3ff"),
  };
}

function hasData(data: ChartData): boolean {
  const xs = data[0];
  return xs !== undefined && xs.length > 0;
}

/** Default x formatter: clock time for `time`, the raw number otherwise. */
function makeDefaultXFormat(x: XKind, xIsMillis: boolean): (value: number) => string {
  if (x === "linear") return (v) => defaultNumberFormat(v);
  return (v) => {
    const ms = xIsMillis ? v : v * 1000;
    const date = new Date(ms);
    return Number.isFinite(ms) ? date.toISOString() : "n/a";
  };
}

export function Chart(props: ChartProps): ReactNode {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const plotRef = useRef<uPlot | null>(null);
  const generatedId = useId();
  const providerKey = useChartSyncKey();
  // A chart without a provider gets its own isolated group so uPlot still has a valid key.
  const syncKey = providerKey ?? `chart-${generatedId}`;

  // Re-build the chart when the theme changes. We observe `data-theme` on <html> and bump a nonce.
  const [themeNonce, setThemeNonce] = useState(0);
  useEffect(() => {
    const target = document.documentElement;
    const observer = new MutationObserver(() => setThemeNonce((n) => n + 1));
    observer.observe(target, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  const [tableOpen, setTableOpen] = useState(false);
  const x = props.x ?? "time";
  const xIsMillis = props.xIsMillis ?? true;
  const height = props.height ?? 220;
  const empty = !hasData(props.data);
  const showChart = !props.loading && !empty;

  // Build/destroy the uPlot instance. Depends on the structural inputs (series shape, size kind,
  // theme, sync group); data-only changes are pushed via setData in the effect below.
  const seriesKey = props.series.map((s) => `${s.label}|${s.colorVar ?? ""}`).join(",");
  useEffect(() => {
    const container = containerRef.current;
    if (container === null || !showChart) return;

    const theme = readTheme();
    const uSeries: uPlot.Series[] = [
      {}, // x series
      ...props.series.map((s): uPlot.Series => {
        const stroke = resolveColor(document.documentElement, s.colorVar, theme.accent);
        const base: uPlot.Series = {
          label: s.label,
          stroke,
          width: s.width ?? 1.5,
          points: { show: false },
        };
        if (s.fillVar !== undefined) {
          return { ...base, fill: resolveColor(document.documentElement, s.fillVar, stroke) };
        }
        return base;
      }),
    ];

    const options: uPlot.Options = {
      width: container.clientWidth || 320,
      height,
      ms: x === "time" && !xIsMillis ? 1 : 1e-3,
      scales: { x: { time: x === "time" } },
      legend: {
        show: true,
        // The legend swatch is DOM (inline style), unlike the canvas line, so it references the
        // theme token (`var(--…)`) instead of baking in a resolved literal colour. The swatch then
        // follows the active theme and the "tokens only, no inline literal colour" invariant holds.
        markers: {
          show: true,
          width: 2,
          dash: "solid",
          stroke: (u, i) => legendMarkerColor(props.series[i - 1]?.colorVar, u, i, "stroke"),
          fill: (u, i) => legendMarkerColor(props.series[i - 1]?.fillVar, u, i, "fill"),
        },
      },
      cursor: {
        // Join the screen's crosshair-sync group; `setSeries` keeps hovered series in step too.
        sync: { key: syncKey, setSeries: true },
      },
      axes: [
        { stroke: theme.text, grid: { stroke: theme.grid }, ticks: { stroke: theme.axis } },
        { stroke: theme.text, grid: { stroke: theme.grid }, ticks: { stroke: theme.axis } },
      ],
      series: uSeries,
    };

    const mutableData = props.data.map((col) => col.slice()) as uPlot.AlignedData;
    const plot = new uPlot(options, mutableData, container);
    plotRef.current = plot;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry === undefined) return;
      const width = Math.max(1, Math.floor(entry.contentRect.width));
      plot.setSize({ width, height });
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      plot.destroy();
      plotRef.current = null;
    };
    // Intentionally keyed on the structural inputs only (series shape via `seriesKey`, size, scale
    // kind, sync group, theme). Data-only changes are pushed by the sibling effect through
    // `setData`, so they must NOT rebuild the instance; `props.data`/`props.series` are therefore
    // read inside but excluded from the dependency list on purpose.
  }, [showChart, seriesKey, height, x, xIsMillis, syncKey, themeNonce]);

  // Push data updates without rebuilding when only the data (not the structure) changed.
  useEffect(() => {
    const plot = plotRef.current;
    if (plot === null || !showChart) return;
    const mutableData = props.data.map((col) => col.slice()) as uPlot.AlignedData;
    plot.setData(mutableData);
  }, [props.data, showChart]);

  const tableId = `${generatedId}-table`;
  const formatY = props.formatY ?? defaultNumberFormat;
  const formatX = props.formatX ?? makeDefaultXFormat(x, xIsMillis);

  return (
    <figure className="chart" aria-label={props.title}>
      {props.loading ? (
        <div className="chart__state" role="status" aria-live="polite">
          <strong>Loading…</strong>
          <span>Fetching series for {props.title}.</span>
        </div>
      ) : empty ? (
        <div className="chart__state" data-kind="empty">
          <strong>No data to plot</strong>
          <span>{props.emptyCause ?? "No data in range."}</span>
        </div>
      ) : (
        <div className="chart__canvas" ref={containerRef} style={{ height }} />
      )}

      {/* The data-table alternative is ALWAYS present (design 12.4): a visually-hidden, fully
          semantic copy kept in the DOM so assistive tech reads the exact values regardless of the
          drawer. The visible "Show data table" control now opens a right-side flyout (DetailDrawer
          pattern) with the same table, instead of expanding an inline dropdown. */}
      {!empty && (
        <>
          <div className="chart__data-actions">
            <button
              type="button"
              className="chart__data-btn"
              aria-haspopup="dialog"
              aria-expanded={tableOpen}
              onClick={() => setTableOpen(true)}
            >
              Show data table
            </button>
          </div>

          {/* Always-present a11y copy (visually hidden). */}
          <div className="sr-only">
            <ChartDataTable
              id={tableId}
              caption={`${props.title} data table`}
              data={props.data}
              series={props.series}
              formatX={formatX}
              formatY={formatY}
            />
          </div>

          {/* The flyout: the same table, in a right-side drawer, opened on demand for sighted users. */}
          <ChartDataDrawer open={tableOpen} onClose={() => setTableOpen(false)} title={props.title}>
            <ChartDataTable
              id={`${tableId}-drawer`}
              caption={`${props.title} data table`}
              data={props.data}
              series={props.series}
              formatX={formatX}
              formatY={formatY}
            />
          </ChartDataDrawer>
        </>
      )}
    </figure>
  );
}

/**
 * A right-side flyout (the shared DetailDrawer pattern, re-used here so a chart can live in the
 * dependency-clean `components/` layer without importing a screen). It is an accessible modal
 * dialog — {@link useDialogFocus} moves focus in, traps Tab, closes on Escape and restores focus to
 * the trigger — and the backdrop click closes it. Rendered only when `open`, so there is no inert
 * dialog in the tree otherwise. Reuses the global `drawer-*` classes so it matches every other
 * drawer in the app.
 */
function ChartDataDrawer(props: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly children: ReactNode;
}): ReactNode {
  const panelRef = useDialogFocus<HTMLDivElement>(props.onClose);
  const headingId = useId();
  if (!props.open) return null;
  return (
    <div className="drawer-root">
      <div className="drawer-backdrop" onClick={props.onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className="drawer-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
      >
        <header className="drawer-head">
          <div className="drawer-head__title">
            <h2 id={headingId} className="drawer-head__heading">Data table</h2>
            <span className="drawer-head__id">{props.title}</span>
          </div>
          <button type="button" className="drawer-head__close" onClick={props.onClose} aria-label="Close data table">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>
        <div className="drawer-body chart__data-drawer-body">
          {props.children}
        </div>
      </div>
    </div>
  );
}

/**
 * The semantic table behind every chart: one row per x value, one column per series. Series whose
 * values are estimated/unverifiable carry the "est." suffix (design 12.3) in the header so a reader
 * knows the whole column's confidence.
 */
function ChartDataTable(props: {
  id: string;
  caption: string;
  data: ChartData;
  series: readonly ChartSeries[];
  formatX: (value: number) => string;
  formatY: (value: number) => string;
}): ReactNode {
  const xs = props.data[0] ?? [];
  return (
    <table id={props.id} className="data-table data-table--chart">
      <caption>{props.caption}</caption>
      <thead>
        <tr>
          <th scope="col">x</th>
          {props.series.map((s, i) => (
            <th key={`${s.label}-${i}`} scope="col">
              {s.label}
              {estimateSuffix(s.measure)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {xs.map((xValue, rowIdx) => (
          <tr key={rowIdx}>
            <th scope="row">{props.formatX(xValue)}</th>
            {props.series.map((s, colIdx) => {
              const col = props.data[colIdx + 1];
              const value = col === undefined ? undefined : col[rowIdx];
              return (
                <td key={`${s.label}-${colIdx}`}>
                  {value === undefined || !Number.isFinite(value) ? "n/a" : props.formatY(value)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
